/** Pawn AI (goal selection) + movement (walk the floor, ride the elevator).
 *  Operates within one building; time-of-day comes from the city clock. */
import type { City, Building, Colonist, Room, RoomType, Activity } from './types';
import { SIM, isNight } from './config';
import { slotX, findRoom } from './world';
import { isNightWorker } from './colonist';
import { urgentNeed } from './needs';

function nearestOfType(b: Building, c: Colonist, now: number, type: RoomType): Room | undefined {
  let best: Room | undefined;
  let bestD = Infinity;
  for (const r of b.rooms) {
    if (r.type !== type || r.damagedUntil > now) continue;
    const d = Math.abs(r.floor - c.floor);
    if (d < bestD) {
      bestD = d;
      best = r;
    }
  }
  return best;
}

interface Goal {
  roomId: number | null;
  act: Activity;
}

function chooseGoal(b: Building, c: Colonist, now: number): Goal {
  if (c.downUntil > now) {
    const bk = nearestOfType(b, c, now, 'bunk');
    return bk ? { roomId: bk.id, act: 'recover' } : { roomId: null, act: 'idle' };
  }
  if (c.breakUntil > now) return { roomId: null, act: 'idle' };

  if (c.activity === 'sleep' && c.needs.rest < 88) {
    const bk = nearestOfType(b, c, now, 'bunk');
    if (bk) return { roomId: bk.id, act: 'sleep' };
  }
  if (c.activity === 'eat' && c.needs.food < 88) {
    const d = nearestOfType(b, c, now, 'diner');
    if (d) return { roomId: d.id, act: 'eat' };
  }
  if (c.activity === 'relax' && c.needs.joy < 85) {
    const r = nearestOfType(b, c, now, 'rec');
    if (r) return { roomId: r.id, act: 'relax' };
  }

  const need = urgentNeed(c);
  if (need) {
    const type: RoomType = need === 'rest' ? 'bunk' : need === 'food' ? 'diner' : 'rec';
    const room = nearestOfType(b, c, now, type);
    if (room) {
      const act: Activity = need === 'rest' ? 'sleep' : need === 'food' ? 'eat' : 'relax';
      return { roomId: room.id, act };
    }
  }

  if (isNight(now) && !isNightWorker(c) && c.needs.rest < 72) {
    const bk = nearestOfType(b, c, now, 'bunk');
    if (bk) return { roomId: bk.id, act: 'sleep' };
  }

  if (c.assignedRoomId != null) {
    const room = findRoom(b, c.assignedRoomId);
    if (room && room.damagedUntil <= now) return { roomId: room.id, act: 'work' };
  }

  return { roomId: null, act: 'idle' };
}

function move(c: Colonist, dt: number): boolean {
  if (Math.abs(c.floor - c.targetFloor) >= 0.05) {
    if (c.x > 0.03) {
      c.x = Math.max(0, c.x - SIM.WALK_SPEED * dt);
    } else {
      const dir = c.targetFloor > c.floor ? 1 : -1;
      c.floor += dir * SIM.ELEVATOR_SPEED * dt;
      if ((dir > 0 && c.floor > c.targetFloor) || (dir < 0 && c.floor < c.targetFloor)) c.floor = c.targetFloor;
    }
    return false;
  }
  c.floor = c.targetFloor;
  if (Math.abs(c.x - c.targetX) > 0.02) {
    const dir = c.targetX > c.x ? 1 : -1;
    c.x += dir * SIM.WALK_SPEED * dt;
    if ((dir > 0 && c.x > c.targetX) || (dir < 0 && c.x < c.targetX)) c.x = c.targetX;
    return false;
  }
  c.x = c.targetX;
  return true;
}

export function stepColonist(city: City, b: Building, c: Colonist, dt: number): void {
  const now = city.t;
  // arrestedReturn === -1 means "gone for good" — always arrested, never times out
  if (c.arrestedReturn === -1 || c.arrestedReturn > now) {
    c.activity = 'arrested';
    return;
  }

  const goal = chooseGoal(b, c, now);

  if (goal.roomId == null) {
    c.targetRoomId = null;
    c.targetFloor = Math.round(c.floor);
    c.targetX = 0.32 + 0.4 * (0.5 + 0.5 * Math.sin(now * 0.25 + c.id));
    move(c, dt);
    c.activity = 'idle';
    return;
  }

  const room = findRoom(b, goal.roomId)!;
  c.targetRoomId = room.id;
  c.targetFloor = room.floor;
  c.targetX = slotX(room.slot);
  const arrived = move(c, dt);
  c.activity = arrived ? goal.act : 'goto';
}
